import React from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Typography,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import { useForm, FormContext, FieldError } from "react-hook-form";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InputField from "../../../components/_Layout/Inputs/InputField";
import { InputType } from "../../../constants/uiConstants/inputConstants";
import CountryAutoComplete from "../../../components/AutoComplete/CountryAutoComplete";
import { IMichiganStateBoardSettings } from "../../../interfaces/setup/reports/IMichiganStateBoardSettings";
import { cloneDeep } from "lodash";
import StatesAutoComplete from "../../../components/AutoComplete/StatesAutoComplete";
import LicensingAgencyAutoComplete from "../../../components/AutoComplete/LicensingAgencyAutoComplete";
import AllStatesAutoComplete from "../../../components/AutoComplete/AllStatesAutoComplete";
import { getProgramVersionsForStateboardSettings } from "../../../api/academicRecords/programVersionApi";
import CustomSnackbar from "../../../components/notifications/CustomSnackbar";
import { CustomSnackBarProps } from "../../../interfaces/common/CustomSnackBarProps";
import ProgressSaveButton from "../../../components/_Layout/Buttons/ProgressSaveButton";
import { deleteMichiganStateBoardReportSettings, getMichiganStateBoardReportProgramCode, getStateBoardAgencyDetail, saveMichiganStateBoardReportSettings } from "../../../api/reports/stateBoard/stateBoardReportApi";
import { IStateBoardAgencyDetail } from "../../../interfaces/setup/reports/IStateBoardAgencyDetail";
import { EmptyGuid } from "../../../utils/constants";


const useStyles = makeStyles((theme: any) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      minHeight: "100%",
      padding: theme.spacing(3),
      overflowX: "hidden",
      overflowY: "auto",
    },
    container: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      height: "100%",
    },
    details: {
      padding: theme.spacing(2, 0.5),
      flex: "1 1",
    },
    componentActions: {
      marginTop: theme.spacing(3),
    },
    wrapper: {
      height: "83%",
      padding: "3px",
      overflowY: 'auto',
      overflowX: 'hidden'
    },
    form: {
      height: "100%",
    },
    titleFont: {
      background: "	rgb(220,220,220)",
      padding: "15px",
      textAlign: "left",
      fontFamily: "sans-serif",
    },
    heading: {
      fontSize: theme.typography.pxToRem(15),
      fontWeight: theme.typography.fontWeightRegular,
    },
    cardTitle: {
      //fontSize: 24,
      color: theme.palette.black,
      textDecoration: "bold",
      backgroundColor: theme.palette.site.secondary,
    },
    titleMargin: {
      margin: theme.spacing(2),
      marginTop: theme.spacing(3)
    },
    licensingAgencyTitleMargin: {
      margin: theme.spacing(2),
    },
    inputField: {
      marginTop: theme.spacing(1.5),
      width:'95%'
    },
    btnDelete: {
      backgroundColor: theme.palette.error.dark,
      marginLeft: theme.spacing(1)
    }
  })
);

interface IProps {
  stateId: string;
  stateDescription: string;
  stateBoardReportId: string;
  stateBoardReportDescription: string;
  campusId: string;
}

interface IStateBoardProgramVersions {
  value: string;
  text: string;
  isChecked?: boolean;
}

const MichiganStateBoardReportSetup = (props: IProps) => {
  const classes = useStyles({});
  const data = useForm<any>({
    mode: "onBlur",
  });

  const michiganStateBoardReportCosmetology = 'Cosmetology State Board Report';
  const michiganStateBoardReportBarber = 'Barber State Board Report';
  const schoolCertificationDefaultText = 'I swear that all statements made in this application are true to the best of my knowledge. Information has not been withheld which might affect the decisions to be made on this application. I am aware that a false statement or dishonest answer may be grounds for denial of this application, disciplinary action, or may be punishable by law. I hereby authorize the Department of Consumer & Industry Services and its agents to investigate any statements made in this application, including checking criminal, civil, and administrative records.\nI understand that it is my responsibility to be aware of and to comply with the provisions set forth in the Cosmetology Act., Michigan Occupational Code, PA 299 of 1980, as amended, and the Rules promulgated thereto.';
  const [programVersions, setProgramVersions] = React.useState<IStateBoardProgramVersions[]>([]);
  const [openConfirmationPopup, setOpenConfirmationPopup] = React.useState<boolean>(false);
  const [snackBarProps, setSnackBarProps] = React.useState<CustomSnackBarProps>(
    {
      showSnackBar: false,
      messageInfo: undefined,
      variant: "info",
    }
  );

  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const selectAllProgramVersionsClick = (isAllChecked: boolean) => {
    if (isAllChecked == true) {
      let copy = cloneDeep(settings);
      copy.mappedProgramIds = programVersions.map(m => m.value);
      setSettings(copy);
    } else {
      let copy = cloneDeep(settings);
      copy.mappedProgramIds = [];
      setSettings(copy);
    }
  }

  const onSelectProgramVersionChange = (isChecked: boolean, programVersionId: string) => {
    if (!!settings && !!settings.mappedProgramIds && settings.mappedProgramIds.findIndex(m => m == programVersionId) >= 0) {
      let copy = cloneDeep(settings);
      let mappedProgramIdsCopy = cloneDeep(copy.mappedProgramIds);
      copy.mappedProgramIds = mappedProgramIdsCopy.filter(m => m != programVersionId);
      setSettings(copy);
    } else {
      if (!!settings) {
        let copy = cloneDeep(settings);
        if (!copy.mappedProgramIds) {
          copy.mappedProgramIds = [];
        }

        copy.mappedProgramIds.push(programVersionId);
        setSettings(copy);
      }
    }
  }

  const handleSubmit = () => {
    data.triggerValidation().then(validationResult => {
      if(validationResult == true && !!props.campusId && !!props.stateBoardReportId && !!props.stateId){
        setIsSaving(true);
        let settingsToPass = cloneDeep(settings);
        settingsToPass.campusId = props.campusId;
        settingsToPass.reportId = props.stateBoardReportId;
        settingsToPass.stateId = props.stateId;
        saveMichiganStateBoardReportSettings(settingsToPass)
        .then(res => {
          setIsSaving(false);
          if(!!res && !!res.result && !!res.result.schoolStateBoardReportId){
            setSnackBarProps({
              showSnackBar: true,
              messageInfo: 'Saved Successfully',
              variant: "success",
            });
            setSettings(res.result);
          }else{
            setSnackBarProps({
              showSnackBar: true,
              messageInfo: 'Failed to save',
              variant: "error",
            });
          }
        })
        .catch(e => {
          setIsSaving(false);
        });
      }else{
        setSnackBarProps({
          showSnackBar: true,
          messageInfo: 'Fill all the required fields',
          variant: "error",
        });
      }
    });
  }

  const handleDelete = (campusId:string, reportId:string, stateId:string) => {
    setOpenConfirmationPopup(true);
  }

  const performDelete = (campusId:string, reportId:string, stateId:string) => {
    if(!!campusId && !!reportId && !!stateId){
      setIsSaving(true);
      deleteMichiganStateBoardReportSettings(campusId, reportId, stateId)
      .then(res => {
        setIsSaving(false);
        if(res == true){
          setSnackBarProps({
            showSnackBar: true,
            messageInfo: 'Deleted Successfully',
            variant: "success",
          });
          getStateBoardReportSettings(reportId, stateId, campusId);
        }else{
          setSnackBarProps({
            showSnackBar: true,
            messageInfo: 'Failed to delete',
            variant: "error",
          });
        }
      }).catch(e => {
        setIsSaving(false);
      }) 
    }
  }

  const initialModel:IMichiganStateBoardSettings = {
    campusCity: null,
    campusCountryId: null,
    campusId: props.campusId,
    campusStateId: null,
    campusZipCode: null,
    licensingAddress1: null,
    licensingAddress2: null,
    licensingAgencyId: null,
    licensingCity: null,
    licensingCountryId: null,
    licensingStateId: null,
    licensingZipCode: null,
    mappedProgramIds: [],
    officerName1: null,
    officerName2: null,
    officerName3: null,
    ownerLicenseNumber: null,
    ownerName: null,
    reportId: props.stateBoardReportId,
    schoolAddress1: null,
    schoolAddress2: null,
    schoolCertification: schoolCertificationDefaultText,
    schoolLicense: null,
    schoolName: null,
    schoolStateBoardReportId: null,
    stateId: null,
    studentRegistrationFee: null,
  };

  const [settings, setSettings] = React.useState<IMichiganStateBoardSettings>(cloneDeep(initialModel));

  const handleChange = (fieldName: any, fieldValue: any) => {
    let copy: any = cloneDeep(settings);
    copy[fieldName] = fieldValue;
    setSettings(copy);

    if(fieldName == 'licensingAgencyId'){
      getAgencyDetails(fieldValue, copy);
    }
  }

  const getStateBoardReportSettings = (reportId:string, stateId:string, campusId:string) =>{
    setIsLoading(true);
    getMichiganStateBoardReportProgramCode(reportId, campusId, stateId)
    .then((res:IMichiganStateBoardSettings) => {
      if(!!res){
        setSettings({...res, schoolCertification: !!res.schoolStateBoardReportId && res.schoolStateBoardReportId != '' && res.schoolStateBoardReportId != EmptyGuid ? res.schoolCertification : schoolCertificationDefaultText});
      }else{
        setToDefault();
      }
    })
    .catch(e => {
      setToDefault();
    });
  }

  const getAgencyDetails = (agencyId:number, copy:IMichiganStateBoardSettings) =>{
    getStateBoardAgencyDetail(agencyId)
    .then((res:IStateBoardAgencyDetail) =>{
      if(!!res){
        copy.licensingAddress1 = res.address1;
        copy.licensingAddress2 = res.address2;
        copy.licensingCountryId = res.countryId;
        copy.licensingCity = res.city;
        copy.licensingStateId = res.stateId;
        copy.licensingZipCode = res.zipCode;
        setSettings(copy);
      }else{
        copy.licensingAddress1 = null;
        copy.licensingAddress2 = null;
        copy.licensingCountryId = undefined;
        copy.licensingCity = null;
        copy.licensingStateId = null;
        copy.licensingZipCode = null;
        setSettings(copy);
      }
    }).catch(e => {
        copy.licensingAddress1 = null;
        copy.licensingAddress2 = null;
        copy.licensingCountryId = undefined;
        copy.licensingCity = null;
        copy.licensingStateId = null;
        copy.licensingZipCode = null;
        setSettings(copy);
    })
  }

  const setToDefault = () =>{
    setSettings(cloneDeep(initialModel));
  }

  React.useEffect(() => {

    if(!!props.campusId && !!props.stateBoardReportId && !!props.stateId){
      getStateBoardReportSettings(props.stateBoardReportId, props.stateId, props.campusId);
    }else{
      setToDefault();
    }

    if (!!props.campusId) {
      getProgramVersionsForStateboardSettings(props.campusId)
        .then((res: IStateBoardProgramVersions[]) => {
          if (!!res && res.length > 0) {
            setProgramVersions(res);
          } else {
            setProgramVersions([]);
          }
        }).catch(e => {
          setProgramVersions([]);
        });
    } else {
      setProgramVersions([]);
    }
  }, [props.campusId, props.stateBoardReportId, props.stateId]);


  return (
    <>
      <CustomSnackbar
        variant={snackBarProps.variant}
        message={snackBarProps.messageInfo}
        open={snackBarProps.showSnackBar}
        onClose={(event?: React.SyntheticEvent, reason?: string) => {
          setSnackBarProps((props: any) => {
            return { ...props, showSnackBar: false };
          });
        }}
      ></CustomSnackbar>
      <div className={classes.wrapper}>
        <Grid
          container
          direction="row"
          justify="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
          <Grid item xs={12} sm={12} md={12}>
            <ExpansionPanel style={{ marginTop: 10 }} defaultExpanded={true}>
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                className={classes.cardTitle}
                style={{ maxHeight: 20 }}
              >
                <Typography className={classes.heading}>
                  Campus Details
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container direction="row" spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>City *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="campusCity"
                          name="campusCity"
                          type={InputType.TEXT}
                          className={classes.inputField}
                          defaultValue={settings?.campusCity ?? ''}
                          error={!!data.errors["campusCity"]}
                          inputRef={data.register({
                            required: "City is required",
                          })}
                          helperText={
                            data.errors["campusCity"]
                              ? data.errors["campusCity"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange(
                              "campusCity",
                              e.target.value
                            );
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>Country *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <CountryAutoComplete
                        filterHandle={(v: any) => {
                          handleChange("campusCountryId", v ? v.value : undefined);
                        }}
                        id="campusCountryId"
                        name="campusCountryId"
                        label=" "
                        valueFilter={
                          settings && settings.campusCountryId && settings.campusCountryId != EmptyGuid
                            ? {
                              key: "value",
                              values: [settings.campusCountryId.toLowerCase()],
                            }
                            : undefined
                        }
                        campusId={props.campusId}
                        countryName="USA"
                        inputRef={data.register({
                          required: "Country is required",
                        })}
                        helperText={
                          !!data.errors["campusCountryId"]
                            ? data.errors["campusCountryId"].message
                            : null
                        }
                        error={!!data.errors["campusCountryId"]}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>State *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <StatesAutoComplete
                        id="campusStateId"
                        name="campusStateId"
                        label={" "}
                        countryId={settings.campusCountryId}
                        valueFilter={
                          settings.campusStateId && settings.campusStateId != EmptyGuid
                            ? {
                              key: "value",
                              values: [settings.campusStateId.toLowerCase()],
                            }
                            : undefined
                        }
                        filterHandle={(v: any) => {
                          handleChange("campusStateId", v ? v.value : undefined);
                        }}
                        inputRef={data.register({
                          required: "State is required",
                        })}
                        helperText={
                          data.errors["campusStateId"]
                            ? data.errors["campusStateId"].message
                            : null
                        }
                        error={!!data.errors.campusStateId}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>Zipcode *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="campusZipCode"
                          name="campusZipCode"
                          type={InputType.TEXT}
                          className={classes.inputField}
                          defaultValue={settings?.campusZipCode ?? ''}
                          error={!!data.errors["campusZipCode"]}
                          inputRef={data.register({
                            required: "Zipcode is required",
                          })}
                          helperText={
                            data.errors["campusZipCode"]
                              ? data.errors["campusZipCode"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange(
                              "campusZipCode",
                              e.target.value
                            );
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>

                  {props.stateBoardReportDescription == michiganStateBoardReportCosmetology && <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>School License</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="schoolLicense"
                          name="schoolLicense"
                          type={InputType.TEXT}
                          className={classes.inputField}
                          defaultValue={settings?.schoolLicense ?? ''}
                          onBlur={(e: any) => {
                            handleChange(
                              "schoolLicense",
                              e.target.value
                            );
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>}

                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            <ExpansionPanel style={{ marginTop: 10 }} defaultExpanded={true}>
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                className={classes.cardTitle}
                style={{ maxHeight: 20 }}
              >
                <Typography className={classes.heading}>
                  State Board Licensing Agency Details
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container direction="row" spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.licensingAgencyTitleMargin}>Licensing agency *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <LicensingAgencyAutoComplete
                        id="licensingAgencyId"
                        name="licensingAgencyId"
                        label={""}
                        stateId={props.stateId}
                        valueFilter={
                          settings.licensingAgencyId
                            ? {
                              key: "value",
                              values: [settings.licensingAgencyId],
                            }
                            : undefined
                        }
                        filterHandle={(v: any) => {
                          handleChange("licensingAgencyId", v ? v.value : undefined);
                        }}
                        inputRef={data.register({
                          required: "Licensing agency is required",
                        })}
                        helperText={
                          data.errors["licensingAgencyId"]
                            ? data.errors["licensingAgencyId"].message
                            : null
                        }
                        error={!!data.errors.licensingAgencyId}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>Licensing agency address 1 *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="licensingAddress1"
                          name="licensingAddress1"
                          type={InputType.TEXT}
                          className={classes.inputField}
                          defaultValue={settings?.licensingAddress1 ?? ''}
                          error={!!data.errors["licensingAddress1"]}
                          inputRef={data.register({
                            required: "Address is required",
                          })}
                          helperText={
                            data.errors["licensingAddress1"]
                              ? data.errors["licensingAddress1"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange(
                              "licensingAddress1",
                              e.target.value
                            );
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>Licensing agency address 2</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="licensingAddress2"
                          name="licensingAddress2"
                          type={InputType.TEXT}
                          className={classes.inputField}
                          defaultValue={settings?.licensingAddress2 ?? ''}
                          onBlur={(e: any) => {
                            handleChange(
                              "licensingAddress2",
                              e.target.value
                            );
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>Country *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <CountryAutoComplete
                        filterHandle={(v: any) => {
                          handleChange("licensingCountryId", v ? v.value : undefined);
                        }}
                        id="licensingCountryId"
                        name="licensingCountryId"
                        label=" "
                        valueFilter={
                          settings && settings.licensingCountryId && settings.licensingCountryId != EmptyGuid
                            ? {
                              key: "value",
                              values: [settings.licensingCountryId.toLowerCase()],
                            }
                            : undefined
                        }
                        campusId={props.campusId}
                        countryName="USA"
                        inputRef={data.register({
                          required: "Country is required",
                        })}
                        helperText={
                          !!data.errors["licensingCountryId"]
                            ? data.errors["licensingCountryId"].message
                            : null
                        }
                        error={!!data.errors["licensingCountryId"]}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>City *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="licensingCity"
                          name="licensingCity"
                          type={InputType.TEXT}
                          className={classes.inputField}
                          defaultValue={settings?.licensingCity ?? ''}
                          error={!!data.errors["licensingCity"]}
                          inputRef={data.register({
                            required: "City is required",
                          })}
                          helperText={
                            data.errors["licensingCity"]
                              ? data.errors["licensingCity"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange(
                              "licensingCity",
                              e.target.value
                            );
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>State *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <AllStatesAutoComplete
                        id="licensingStateId"
                        name="licensingStateId"
                        label={" "}
                        valueFilter={
                          settings.licensingStateId && settings.licensingStateId != EmptyGuid
                            ? {
                              key: "value",
                              values: [settings.licensingStateId.toLowerCase()],
                            }
                            : undefined
                        }
                        filterHandle={(v: any) => {
                          handleChange("licensingStateId", v ? v.value : undefined);
                        }}
                        inputRef={data.register({
                          required: "State is required",
                        })}
                        helperText={
                          data.errors["licensingStateId"]
                            ? data.errors["licensingStateId"].message
                            : null
                        }
                        error={!!data.errors.licensingStateId}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={2}>
                      <Typography className={classes.titleMargin}>Zipcode *</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <InputField
                        id="licensingZipCode"
                        name="licensingZipCode"
                        type={InputType.TEXT}
                        className={classes.inputField}
                        defaultValue={settings?.licensingZipCode ?? ''}
                        error={!!data.errors["licensingZipCode"]}
                        inputRef={data.register({
                          required: "Zipcode is required",
                        })}
                        helperText={
                          data.errors["licensingZipCode"]
                            ? data.errors["licensingZipCode"].message
                            : null
                        }
                        onBlur={(e: any) => {
                          handleChange(
                            "licensingZipCode",
                            e.target.value
                          );
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            <ExpansionPanel style={{ marginTop: 10 }} defaultExpanded={true}>
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                className={classes.cardTitle}
                style={{ maxHeight: 20 }}
              >
                <Typography className={classes.heading}>
                  Approved Program Versions
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container direction="row" spacing={2}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Program</TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={programVersions.length == settings.mappedProgramIds.length && !!programVersions && programVersions.length > 0}
                            id="selectAllProgramVersions"
                            onChange={(e: any) => {
                              selectAllProgramVersionsClick(e.target.checked);
                            }}
                            color="primary"
                          />Select All
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!!programVersions && programVersions.length > 0 && programVersions.map(pv => {
                        return <TableRow key={`${pv.value}`}>
                          <TableCell align="center">{pv.text}</TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={!!settings && !!settings.mappedProgramIds && settings.mappedProgramIds.includes(pv.value)}
                              id={`selectProgramVersion_${pv.value}`}
                              onChange={(e: any) => {
                                onSelectProgramVersionChange(e.target.checked, pv.value);
                              }}
                              color="primary"
                            />
                          </TableCell>
                        </TableRow>
                      })}
                    </TableBody>
                  </Table>
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>
            {props.stateBoardReportDescription == michiganStateBoardReportCosmetology && <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography><strong>School Certification:</strong></Typography>
              </Grid>
              <Grid item xs={12}>
                <textarea
                  name="schoolCertification"
                  rows={8}
                  style={{ width: '98%' }}
                  value={settings.schoolCertification ?? ''}
                  onChange={(e: any) => {
                    handleChange('schoolCertification', e.target.value)
                  }}
                />
              </Grid>
            </Grid>}

            {props.stateBoardReportDescription == michiganStateBoardReportCosmetology && <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography><strong>Student Registration - Fee:</strong></Typography>
              </Grid>
              <Grid item xs={4}>
                <InputField
                  id="studentRegistrationFee"
                  name="studentRegistrationFee"
                  type={InputType.NUMBER}
                  decimal
                  decimalPlaces={2}
                  
                  defaultValue={settings?.studentRegistrationFee ?? ''}
                  onBlur={(e: any) => {
                    handleChange(
                      "studentRegistrationFee",
                      e.target.value
                    );
                  }}
                />
              </Grid>
            </Grid>}
          </Grid>
        </Grid>

      </div>

      <div className={classes.componentActions}>
        <ProgressSaveButton
          key="btnSaveMichiganStateBoardSettings"
          text="Save"
          onClick={handleSubmit}
          loading={isSaving}
          size="small"
        ></ProgressSaveButton>

        <ProgressSaveButton
          key={"btnDeleteMichiganStateBoardSettings"}
          text="Delete"
          onClick={handleDelete}
          loading={isSaving}
          size="small"
          buttonClassName={classes.btnDelete}
        />
      </div>

      <Dialog
          disableBackdropClick
          disableEscapeKeyDown
          maxWidth="xs"
          aria-labelledby="confirmation-dialog"
          open={openConfirmationPopup}
          onClose={() => {
 
          }}
        >
          <DialogContent>
            <Typography>Are you sure you want to delete this setting?</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                performDelete(props.campusId, props.stateBoardReportId, props.stateId);
                setOpenConfirmationPopup(false);
              }}
              color="primary"
            >
              {"YES"}
            </Button>
            <Button
                autoFocus
                onClick={() => {
                  setOpenConfirmationPopup(false);
                }}
                color="primary"
              >
                NO
              </Button>
          </DialogActions>
        </Dialog>
        
    </>
  );
};

export default MichiganStateBoardReportSetup;
